import { useCallback, useRef } from 'react';
import { useScheduleStore, type ScheduleItem } from '../stores/scheduleStore';
import { useWorkloadStore } from '../stores/workloadStore';
import {
  getCapacitySchemes,
  type DaySchedule,
  DEFAULT_DAY_SCHEDULE,
} from '../api/capacity.api';
import type { LeaveEntry } from '../stores/leaveStore';
import {
  fetchUserSchedules,
  invalidateScheduleCache,
  computeSchedulerWorkload,
  type SchedulerHolidayEntry,
} from '../services/workload-data.service';
import { getWorklogSummaryByItemAndUser, getBulkWorklogs, type Worklog } from '../api/worklog.api';

// ─── Re-export invalidateScheduleCache as invalidateCapacityCache for compat ─

/** @deprecated Use invalidateScheduleCache() from workload-data.service instead */
export const invalidateCapacityCache = invalidateScheduleCache;

// ─── requestIdleCallback with Safari fallback ───────────────────────────────

const _scheduleCompute: (cb: () => void) => number =
  typeof requestIdleCallback === 'function'
    ? requestIdleCallback
    : (cb: () => void) => setTimeout(cb, 0) as unknown as number;

const _cancelCompute: (id: number) => void =
  typeof cancelIdleCallback === 'function'
    ? cancelIdleCallback
    : (id: number) => clearTimeout(id);

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Extract yyyy-MM-dd from an ISO timestamp using local time.
 *  The dateRange store values represent local dates (e.g., Monday midnight local),
 *  so local extraction gives the correct calendar date. */
function toDateString(isoTimestamp: string): string {
  const d = new Date(isoTimestamp);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Hook that computes per-user, per-day workload from the schedule store items
 * and capacity schemes fetched from the backend.
 *
 * Delegates all computation to the centralised workload-data service,
 * keeping only the React lifecycle + requestIdleCallback orchestration here.
 *
 * Performance optimizations:
 * - Capacity schemes are cached (1 min TTL) via the shared service
 * - Heavy computation is deferred via requestIdleCallback to avoid blocking the main thread
 * - Sequential rapid calls are coalesced (only the latest wins)
 * - Results are stored with a pre-built userId index for O(1) per-row lookup
 */
export function useWorkload() {
  // Track the latest computation request so we can discard stale results
  const computeIdRef = useRef(0);
  // Track pending idle callback to cancel on new request
  const idleHandleRef = useRef<number | null>(null);
  // Cache worklog data to avoid re-fetching when only items change
  const worklogCacheRef = useRef<{
    indicatorType: string | null;
    dateKey: string;
    boardKey: string;
    userKey: string;
    timestamp: number;
    worklogsByUserDate: Map<string, number>;
    worklogsByItem: Map<string, number>;
    rawWorklogs: Worklog[];
  } | null>(null);

  /** Worklog cache TTL — 1 minute. Ensures stale data (e.g. worklogs deleted by another user) is refreshed. */
  const WORKLOG_CACHE_TTL_MS = 60_000;

  /** In-flight worklog fetch promise — prevents concurrent computeWorkload calls from duplicating API requests */
  const worklogFetchPromiseRef = useRef<Promise<{ worklogsByUserDate: Map<string, number>; worklogsByItem: Map<string, number>; rawWorklogs: Worklog[] }> | null>(null);

  const computeWorkload = useCallback(async (
    visibleUserIds?: number[],
    filteredItems?: ScheduleItem[],
    approvedLeaves?: LeaveEntry[],
    holidays?: SchedulerHolidayEntry[],
  ) => {
    const { dateRange } = useScheduleStore.getState();
    const items = filteredItems ?? useScheduleStore.getState().items;

    // Collect all user IDs that need capacity data
    const allUserIds = new Set(items.map((item) => item.assigneeId));
    allUserIds.delete(0);
    if (visibleUserIds) {
      for (const uid of visibleUserIds) {
        if (uid !== 0) allUserIds.add(uid);
      }
    }

    if (allUserIds.size === 0) {
      const { setEntries, setIssueCountEntries } = useWorkloadStore.getState();
      setEntries([]);
      setIssueCountEntries([]);
      return;
    }

    // Snapshot store state BEFORE any async operations to avoid race conditions.
    // If the user toggles mode during an await, we still use the mode that
    // triggered this computation, not the new one.
    const { perPersonDistribution, indicatorType } = useWorkloadStore.getState();

    // Fetch capacity via shared service (cached after first call)
    const stringUserIds = Array.from(allUserIds).map(String);
    const { userSchedules, defaultSchedule } = await fetchUserSchedules(stringUserIds);

    // Convert string-keyed map to number-keyed map for the scheduler computation
    const userCapacityMap = new Map<number, Record<string, number>>();
    for (const [strId, schedule] of userSchedules) {
      userCapacityMap.set(Number(strId), schedule);
    }

    // Store default schedule and per-user schedules for non-working day coloring
    if (!useWorkloadStore.getState().defaultSchedule) {
      useWorkloadStore.getState().setDefaultSchedule(
        defaultSchedule as DaySchedule,
      );
    }
    useWorkloadStore.getState().setUserSchedules(userCapacityMap);

    // Fetch worklog data when remaining estimate modes are active
    const isRemainingMode = indicatorType === 'remaining_estimate' || indicatorType === 'worklog_workload' || indicatorType === 'availability';
    let worklogsByUserDate: Map<string, number> | undefined;

    // Per-item total worklogs for remaining estimate computation
    let worklogsByItem: Map<string, number> | undefined;

    if (isRemainingMode) {
      const inputStart = toDateString(dateRange.start);
      const inputEnd = toDateString(dateRange.end);
      const dateKey = `${inputStart}_${inputEnd}`;

      // Reuse cached worklog data if indicator type, date range, visible boards,
      // and visible users haven't changed. Board IDs and user IDs are included
      // because on page load items/users may not be available yet, and filtering
      // by team/user changes which worklogs are relevant.
      const boardIdSet = new Set<string>();
      for (const item of items) {
        if (item.boardId) boardIdSet.add(String(item.boardId));
      }
      const boardKey = Array.from(boardIdSet).sort().join(',');
      const userKey = stringUserIds.slice().sort().join(',');

      const cache = worklogCacheRef.current;
      const cacheAlive = cache && (Date.now() - cache.timestamp) < WORKLOG_CACHE_TTL_MS;
      if (cacheAlive && cache.indicatorType === indicatorType && cache.dateKey === dateKey && cache.boardKey === boardKey && cache.userKey === userKey) {
        worklogsByUserDate = cache.worklogsByUserDate;
        worklogsByItem = cache.worklogsByItem;
      } else {
        // Deduplicate: if a fetch is already in flight, reuse its promise
        if (!worklogFetchPromiseRef.current) {
          worklogFetchPromiseRef.current = (async () => {
            const wlByUserDate = new Map<string, number>();
            const wlByItem = new Map<string, number>();

            const [rawWorklogs, itemUserSummaries] = await Promise.all([
              getBulkWorklogs({
                userIds: stringUserIds,
                startDate: inputStart,
                endDate: inputEnd,
              }),
              boardIdSet.size > 0
                ? getWorklogSummaryByItemAndUser(
                    Array.from(boardIdSet),
                    stringUserIds,
                  )
                : Promise.resolve([]),
            ]);

            for (const wl of rawWorklogs) {
              const dateStr = wl.date.slice(0, 10);
              const key = `${wl.mondayUserId}_${dateStr}`;
              wlByUserDate.set(key, (wlByUserDate.get(key) ?? 0) + wl.hours);
            }

            for (const summary of itemUserSummaries) {
              const key = summary.mondayItemId;
              wlByItem.set(key, (wlByItem.get(key) ?? 0) + summary.totalHours);
            }

            return { worklogsByUserDate: wlByUserDate, worklogsByItem: wlByItem, rawWorklogs };
          })();
        }

        try {
          const fetchResult = await worklogFetchPromiseRef.current;
          worklogsByUserDate = fetchResult.worklogsByUserDate;
          worklogsByItem = fetchResult.worklogsByItem;

          useWorkloadStore.getState().setRawWorklogs(fetchResult.rawWorklogs);

          // Cache for reuse (expires after WORKLOG_CACHE_TTL_MS)
          worklogCacheRef.current = {
            indicatorType: indicatorType ?? null,
            dateKey,
            boardKey,
            userKey,
            timestamp: Date.now(),
            worklogsByUserDate,
            worklogsByItem,
            rawWorklogs: fetchResult.rawWorklogs,
          };
        } catch (err) {
          console.warn('[useWorkload] Failed to fetch worklogs:', err);
          worklogsByUserDate = new Map();
          worklogsByItem = new Map();
        } finally {
          worklogFetchPromiseRef.current = null;
        }
      }

      // Store worklogsByItem so components can compute per-item remaining estimates
      useWorkloadStore.getState().setWorklogsByItem(worklogsByItem);
    } else {
      // Clear when not in remaining mode
      if (useWorkloadStore.getState().worklogsByItem.size > 0) {
        useWorkloadStore.getState().setWorklogsByItem(new Map());
      }
    }

    // Bump the compute ID — any pending idle callback with an older ID will be discarded
    const currentId = ++computeIdRef.current;

    // Cancel any previously scheduled idle callback
    if (idleHandleRef.current !== null) {
      _cancelCompute(idleHandleRef.current);
      idleHandleRef.current = null;
    }

    // Capture input snapshot synchronously
    // dateRange stores full ISO timestamps (e.g. "2026-02-08T22:00:00.000Z");
    // computeSchedulerWorkload expects yyyy-MM-dd date strings.
    const inputItems = items;
    const inputLeaves = approvedLeaves ?? [];
    const inputHolidays = holidays ?? [];
    const inputStart = toDateString(dateRange.start);
    const inputEnd = toDateString(dateRange.end);

    idleHandleRef.current = _scheduleCompute(() => {
      idleHandleRef.current = null;

      // Discard if a newer computation was requested while we waited
      if (computeIdRef.current !== currentId) return;

      const result = computeSchedulerWorkload(
        {
          items: inputItems,
          approvedLeaves: inputLeaves,
          holidays: inputHolidays,
          allUserIds,
          dateRangeStart: inputStart,
          dateRangeEnd: inputEnd,
          perPersonDistribution,
          indicatorType: indicatorType ?? undefined,
          worklogsByUserDate,
          worklogsByItem,
        },
        userCapacityMap,
      );

      // Double-check we're still the latest before writing to store
      if (computeIdRef.current !== currentId) return;

      const { setEntries, setIssueCountEntries } = useWorkloadStore.getState();
      setEntries(result.entries);
      setIssueCountEntries(result.issueEntries);
    });
  }, []);

  const fetchDefaultSchedule = useCallback(async () => {
    if (useWorkloadStore.getState().defaultSchedule) return;
    try {
      const schemes = await getCapacitySchemes();
      const def = schemes.find((s) => s.is_default);
      useWorkloadStore.getState().setDefaultSchedule(def?.schedule ?? DEFAULT_DAY_SCHEDULE);
    } catch {
      useWorkloadStore.getState().setDefaultSchedule(DEFAULT_DAY_SCHEDULE);
    }
  }, []);

  /** Invalidate cached worklog data — call when worklogs are created/deleted. */
  const invalidateWorklogCache = useCallback(() => {
    worklogCacheRef.current = null;
  }, []);

  return { computeWorkload, fetchDefaultSchedule, invalidateWorklogCache };
}
